import { FC as ReactFC, useState } from 'react';

import BlockUi from 'react-block-ui';
import * as intl from 'react-intl-universal';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardText,
  CardTitle,
  Col,
  Row,
} from 'reactstrap';

import ImageComponent from 'shared/components/image/ImageComponent';
import Status from 'shared/enums/Status';
import MfaRecoveryIllustration from 'shared/static/img/mfa-recovery.svg';
import MfaRecoveryThumb from 'shared/static/img/thumbs/mfa-recovery.png';

import MfaRecoveryGenerationModal from './mfa-recovery-generation-modal/MfaRecoveryGenerationModal';
import styles from './mfaRecovery.module.scss';
import MfaRecoveryProps from './MfaRecoveryProps';

const INITIAL_RECOVERY_CODES = [];

const MfaRecovery: ReactFC<MfaRecoveryProps> = (props: MfaRecoveryProps) => {
  const {
    mfaEnabled,
    recoveryCodeAge,
    recoveryCodesGenerated,
    status,
    onGenerateRecoveryCodes,
  } = props;

  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [mfaRecoveryGenerationModalOpen, setMfaRecoveryGenerationModalOpen] =
    useState<boolean>(false);

  const badgeText =
    recoveryCodeAge === null
      ? ''
      : intl.get('LBL_MFA_RECOVERY_LAST_GENERATED', { days: recoveryCodeAge });

  const onToggleMfaRecoveryGenerationModal = (): void => {
    setMfaRecoveryGenerationModalOpen((prev) => {
      if (prev) setRecoveryCodes(INITIAL_RECOVERY_CODES);
      return !prev;
    });
  };

  const handleGenerateRecoveryCodes = async (): Promise<void> => {
    const { status: actionStatus, recoveryCodes: codes } =
      await onGenerateRecoveryCodes();
    if (actionStatus === Status.Success) {
      setRecoveryCodes(codes);
      setMfaRecoveryGenerationModalOpen(true);
    }
  };

  return (
    <>
      <BlockUi
        tag="div"
        blocking={status === Status.Loading}
        className="row dashboard-row"
      >
        <Col>
          <div className="content-box lower-margins">
            <div className="content-box-title d-flex flex-column align-items-start pb-4 pt-2">
              <h3 className="main-title">
                {intl.get('LBL_MFA_RECOVERY_SECTION_TITLE')}
              </h3>
              <p className="text-14-light text-gray mb-0 mt-2">
                {intl.get('LBL_MFA_RECOVERY_SECTION_DESCRIPTION')}
              </p>
            </div>
            <Row className="d-flex flex-wrap justify-content-start">
              <Col xs="auto" className="mb-4 w-100">
                <Card
                  data-cy="mfa-recovery-card"
                  data-testid="mfa-method-card"
                  className={`${styles.mfaRecoveryCard}`}
                  tabIndex={0}
                >
                  {recoveryCodesGenerated && (
                    <Badge
                      color="warning"
                      className={`text-12-medium ${styles.mfaRecoveryGenerationBadge}`}
                    >
                      {badgeText}
                    </Badge>
                  )}
                  <CardBody className={styles.mfaRecoveryCardBody}>
                    <Col
                      className={`d-flex align-items-center ${styles.mfaRecoveryText}`}
                    >
                      <ImageComponent
                        loading="eager"
                        src={MfaRecoveryIllustration}
                        className={`${styles.mfaRecoveryImg}`}
                        alt="MFA Recovery"
                        thumb={MfaRecoveryThumb}
                      />
                      <div className="ml-3">
                        <CardTitle className="text-16-bold">
                          {intl.get('LBL_MFA_RECOVERY_CARD_TITLE')}
                        </CardTitle>
                        <CardText className="text-14-light text-gray">
                          {intl.get('LBL_MFA_RECOVERY_CARD_DESCRIPTION')}
                        </CardText>
                      </div>
                    </Col>
                    <Col
                      className={`justify-content-end ${styles.mfaRecoveryBtn}`}
                    >
                      <Button
                        className="btn btn-secondary"
                        onClick={handleGenerateRecoveryCodes}
                        disabled={!mfaEnabled}
                      >
                        {intl.get('BTN_MFA_GENERATE_CODES')}
                      </Button>
                    </Col>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </div>
        </Col>
      </BlockUi>
      <MfaRecoveryGenerationModal
        isOpen={mfaRecoveryGenerationModalOpen}
        status={status}
        recoveryCodes={recoveryCodes}
        onToggle={onToggleMfaRecoveryGenerationModal}
      />
    </>
  );
};

export default MfaRecovery;
